'use client'

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { useSession } from 'next-auth/react'

interface NotificationContextType {
  unreadCount: number
  loading: boolean
  refreshUnreadCount: () => void
  decrementUnreadCount: (count?: number) => void
  resetUnreadCount: () => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

interface NotificationProviderProps {
  children: ReactNode
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const { data: session, status } = useSession()

  const fetchUnreadCount = useCallback(async () => {
    // Only fetch if user is authenticated
    if (status !== 'authenticated' || !session?.user) {
      setLoading(false)
      return
    }

    try {
      const response = await fetch('/api/user/notifications?unreadOnly=true&limit=1')
      if (response.ok) {
        const data = await response.json()
        setUnreadCount(data.pagination?.total || 0)
      } else if (response.status === 401) {
        // If unauthorized, reset count and don't log error
        setUnreadCount(0)
      }
    } catch (error) {
      console.error('Failed to fetch unread notifications count:', error)
    } finally {
      setLoading(false)
    }
  }, [status, session?.user])

  useEffect(() => {
    // Only start fetching when authentication status is determined
    if (status === 'loading') return

    fetchUnreadCount()
    
    // Only set up interval if authenticated
    if (status === 'authenticated' && session?.user) {
      // 每30秒检查一次未读消息
      const interval = setInterval(fetchUnreadCount, 30000)
      
      return () => clearInterval(interval)
    }
  }, [status, session, fetchUnreadCount])

  // 手动刷新未读数量
  const refreshUnreadCount = useCallback(() => {
    // Only refresh if authenticated
    if (status === 'authenticated' && session?.user) {
      fetchUnreadCount()
    }
  }, [status, session?.user, fetchUnreadCount])

  // 减少未读数量（当用户阅读消息时）
  const decrementUnreadCount = (count = 1) => {
    setUnreadCount(prev => Math.max(0, prev - count))
  }

  // 重置未读数量（全部标记为已读时）
  const resetUnreadCount = () => {
    setUnreadCount(0)
  }

  const value = {
    unreadCount,
    loading,
    refreshUnreadCount,
    decrementUnreadCount,
    resetUnreadCount
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}